import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../api.js';
import { useLang } from '../i18n.js';
import { thb, thbShort, periodNow, relTime } from '../money.js';
import { Card, Kpi, Empty, Spinner, Avatar, SplitBar, PeriodStepper } from '../ui.jsx';

/**
 * Monthly Review.
 *
 * One person, one month, one page. A rep sees their own card; an admin sees the
 * whole team on the same page and publishes the month when it is agreed.
 *
 * The screen never computes a number from raw records — every figure comes from
 * the API's rollups, so what a rep reads here and what the admin publishes are
 * the same arithmetic. The only thing this file owns is the written note.
 */
export default function Review({ me, toast }) {
  const { t, lang } = useLang();
  const [period, setPeriod] = useState(periodNow);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setData(null);
    setError('');
    api
      .review(period)
      .then(setData)
      .catch((e) => setError(e.message));
  }, [period]);

  useEffect(() => {
    load();
  }, [load]);

  const current = periodNow();
  const isCurrent = period >= current;

  const publish = async () => {
    setBusy(true);
    try {
      await api.publishReview(period);
      toast(t('monthPublished'));
      load();
    } catch (e) {
      toast(e.message);
    } finally {
      setBusy(false);
    }
  };

  const reopen = async () => {
    setBusy(true);
    try {
      await api.unpublishReview(period);
      toast(t('monthReopened'));
      load();
    } catch (e) {
      toast(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
          marginBottom: 20,
        }}
      >
        <div>
          <div className="display" style={{ fontSize: 26 }}>
            {t('navReview')}
          </div>
          <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 3 }}>
            {t('reviewBlurb')}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <PeriodStepper period={period} onChange={setPeriod} max={current} />
          {data?.team_view &&
            (data.team.published ? (
              <button className="btn" disabled={busy} onClick={reopen}>
                {t('reopenMonth')}
              </button>
            ) : (
              <button
                className="btn btn-primary"
                disabled={busy || isCurrent}
                onClick={publish}
                title={isCurrent ? t('publishNotFinished') : undefined}
              >
                {t('publishMonth')}
              </button>
            ))}
        </div>
      </div>

      {error && <div className="page">{error}</div>}
      {!error && !data && <Spinner label={t('loading')} />}

      {data && (
        <>
          {data.team_view && (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
              <Kpi label={t('teamTotal')} value={thbShort(data.team.won_total_satang)} />
              <Kpi
                label={t('attainment')}
                value={
                  data.team.attainment_pct === null ? '—' : `${Math.round(data.team.attainment_pct)}%`
                }
              />
              <Kpi label={t('dealsWon')} value={data.team.deals_won} />
              <Kpi
                label={t('winRate')}
                value={data.team.win_rate_pct === null ? '—' : `${data.team.win_rate_pct}%`}
              />
              <Kpi label={t('callsLogged')} value={data.team.calls} />
              <Kpi label={t('briefsIn')} value={data.team.briefs} />
            </div>
          )}

          {isCurrent && (
            <div
              style={{
                fontSize: 12.5,
                color: 'var(--ink-3)',
                marginBottom: 14,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: 'var(--warning)',
                  flexShrink: 0,
                }}
              />
              {t('monthStillRunning')}
            </div>
          )}

          {data.people.length === 0 ? (
            <Empty>{t('noReviewRows')}</Empty>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {data.people.map((p) => (
                <PersonCard
                  key={p.user.id}
                  person={p}
                  period={period}
                  previousPeriod={data.previous_period}
                  canEdit={
                    (p.user.id === me.id || me.role === 'admin') && p.status !== 'published'
                  }
                  onSaved={load}
                  toast={toast}
                  t={t}
                  lang={lang}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PersonCard({ person: p, period, previousPeriod, canEdit, onSaved, toast, t, lang }) {
  const [notes, setNotes] = useState(p.notes || '');
  const [saving, setSaving] = useState(false);
  const dirty = notes !== (p.notes || '');

  // The card is remounted with a fresh key on reload, so local state cannot
  // drift from the server — but a period change reuses the component.
  useEffect(() => {
    setNotes(p.notes || '');
  }, [p.notes, period]);

  const denom = Math.max(p.quota_total_satang, p.won_total_satang, 1);

  const save = async (status) => {
    setSaving(true);
    try {
      await api.saveReviewNote(period, { user_id: p.user.id, notes, status });
      toast(status === 'submitted' ? t('reviewSubmitted') : t('noteSaved'));
      onSaved();
    } catch (e) {
      toast(e.message);
    } finally {
      setSaving(false);
    }
  };

  const statusPill = () => {
    const map = {
      published: ['var(--success)', 'var(--success-soft)', t('published')],
      submitted: ['var(--info)', 'var(--primary-soft)', t('submitted')],
      draft: ['var(--ink-3)', 'var(--sunken)', t('draft')],
    };
    const [fg, bg, label] = map[p.status] || map.draft;
    return (
      <span className="pill" style={{ background: bg, color: fg }}>
        {label}
      </span>
    );
  };

  return (
    <Card style={{ padding: 0 }}>
      {/* ------------------------------------------------------------- header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '14px 18px',
          borderBottom: '1px solid var(--line)',
          flexWrap: 'wrap',
        }}
      >
        <Avatar initials={p.user.avatar_initials} seed={p.user.avatar_var} size={30} />
        <span>
          <span style={{ display: 'block', fontWeight: 700, fontSize: 14 }}>{p.user.name}</span>
          <span style={{ display: 'block', fontSize: 11.5, color: 'var(--ink-3)' }}>
            {p.user.role}
            {p.entities.length ? ` · ${[...new Set(p.entities)].join(' + ')}` : ''}
          </span>
        </span>
        {statusPill()}

        <span style={{ marginLeft: 'auto', textAlign: 'right' }}>
          <span className="display num" style={{ display: 'block', fontSize: 22, lineHeight: 1.1 }}>
            {thb(p.won_total_satang)}
          </span>
          <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
            {p.attainment_pct === null
              ? t('noQuota')
              : `${Math.round(p.attainment_pct)}% ${t('ofQuotaShort', {
                  q: thbShort(p.quota_total_satang),
                })}`}
            {p.change_pct !== null && (
              <span
                style={{
                  marginLeft: 6,
                  fontWeight: 700,
                  color: p.change_pct >= 0 ? 'var(--success)' : 'var(--danger)',
                }}
              >
                {p.change_pct >= 0 ? '+' : ''}
                {p.change_pct}%
              </span>
            )}
          </span>
        </span>
      </div>

      {/* ---------------------------------------------------------- the money */}
      <div style={{ padding: '14px 18px 4px' }}>
        <SplitBar
          newPct={(p.won_new_satang / denom) * 100}
          upPct={(p.won_upsell_satang / denom) * 100}
        />
        <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 11.5, flexWrap: 'wrap' }}>
          <Legend color="var(--primary)" label={t('newCust')} value={thb(p.won_new_satang)} />
          <Legend
            color="var(--mr-cyan-deep)"
            label={t('upsell')}
            value={thb(p.won_upsell_satang)}
          />
          {p.previous_won_satang > 0 && (
            <span style={{ color: 'var(--ink-3)', marginLeft: 'auto' }}>
              {t('vsPrev', { p: previousPeriod })} {thbShort(p.previous_won_satang)}
            </span>
          )}
        </div>
      </div>

      {/* ----------------------------------------------------- what produced it */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))',
          gap: 2,
          padding: '14px 18px',
        }}
      >
        <Stat label={t('callsLogged')} value={p.calls} />
        <Stat label={t('meetings')} value={p.meetings} />
        <Stat label={t('briefsIn')} value={p.briefs} />
        <Stat label={t('quotesOut')} value={p.quotes} />
        <Stat label={t('dealsWon')} value={p.deals_won} />
        <Stat label={t('dealsLost')} value={p.deals_lost} />
        <Stat label={t('avgDeal')} value={p.deals_won ? thbShort(p.avg_deal_satang) : '—'} />
        <Stat
          label={t('avgCycle')}
          value={p.avg_cycle_days === null ? '—' : t('nDays', { n: p.avg_cycle_days })}
        />
        <Stat label={t('pipelineCreated')} value={thbShort(p.pipeline_created_satang)} />
      </div>

      {/* --------------------------------------------------------------- note */}
      <div style={{ padding: '4px 18px 16px', borderTop: '1px solid var(--line)' }}>
        <label className="field-label" style={{ marginTop: 12 }}>
          {t('reviewNote')}
        </label>
        {canEdit ? (
          <>
            <textarea
              className="textarea"
              rows={3}
              value={notes}
              placeholder={t('notePlaceholder')}
              onChange={(e) => setNotes(e.target.value)}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
              <button
                className="btn btn-sm"
                disabled={saving || !dirty}
                onClick={() => save('draft')}
              >
                {t('saveDraft')}
              </button>
              <button
                className="btn btn-sm btn-primary"
                disabled={saving || !notes.trim()}
                onClick={() => save('submitted')}
              >
                {t('submitReview')}
              </button>
              {dirty && (
                <span style={{ fontSize: 11.5, color: 'var(--warning)' }}>{t('unsaved')}</span>
              )}
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                fontSize: 13,
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                color: notes ? 'var(--ink)' : 'var(--ink-3)',
              }}
            >
              {notes || t('noNoteWritten')}
            </div>
            {p.status === 'published' && (
              <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 8 }}>
                {p.published_by_name
                  ? t('publishedBy', { name: p.published_by_name })
                  : t('published')}
                {p.published_at ? ` · ${relTime(p.published_at, lang)}` : ''}
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

const Stat = ({ label, value }) => (
  <div style={{ minWidth: 0 }}>
    <div
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '.06em',
        textTransform: 'uppercase',
        color: 'var(--ink-3)',
      }}
    >
      {label}
    </div>
    <div className="num" style={{ fontSize: 15, fontWeight: 600, marginTop: 2 }}>
      {value}
    </div>
  </div>
);

const Legend = ({ color, label, value }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--ink-2)' }}>
    <span style={{ width: 8, height: 8, borderRadius: 2, background: color }} />
    {label} <strong className="num">{value}</strong>
  </span>
);
